import type { ReactNode } from 'react'
import React from 'react'

import type { BillingCadence, Deal, LineItemType, PricingRegion, PricingTerm } from '../../types/deal'

import type { BillingTypeOption, DealDraft, DealDraftValidation, LineItemDraft } from './types'

export function toISO(prettyMDY: string): string {
  if (!prettyMDY) return ''
  const parts = prettyMDY.split('/').map((segment) => parseInt(segment, 10))
  if (parts.length !== 3 || parts.some((segment) => Number.isNaN(segment))) return ''
  const [month, day, year] = parts
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function isoToPretty(iso: string): string {
  if (!iso) return ''
  const [year, month, day] = iso.split('-')
  if (!year || !month || !day) return ''
  return `${month}/${day}/${year}`
}

export function billingCadenceToOption(cadence: BillingCadence | undefined): BillingTypeOption {
  switch (cadence) {
    case 'Quarterly':
      return 'quarterly'
    case 'Monthly':
      return 'monthly'
    case 'OTP':
      return 'onetime'
    case 'Annual':
    default:
      return 'annual'
  }
}

export function billingOptionToCadence(option: BillingTypeOption): BillingCadence {
  return ({ annual: 'Annual', quarterly: 'Quarterly', monthly: 'Monthly', onetime: 'OTP' } as const)[
    option
  ]
}

export function badgeForSub(sub: LineItemType): {
  className: string
  label: 'SW' | 'Lease' | 'OTP'
} {
  if (sub === 'sw') return { className: 'badge-sw', label: 'SW' }
  if (sub === 'hw') return { className: 'badge-hw', label: 'Lease' }
  return { className: 'badge-otp', label: 'OTP' }
}

export function uid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `id_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

const Y_SERIES_SERIALS = [
  'YAL2N13E3533A001',
  'YAL2N13E3533A002',
  'YAL2N13E3533A003',
  'YAL2N13E3533A004',
  'YAL2N13E3533A005',
]

const HW_CAMERA_SERIALS = [
  '6C20095',
  '6C20082',
  '6C20079',
  '6C20080',
  '6C20081',
  '6C20101',
]

const NEW_HW_DEVICE_CATALOG: string[] = (() => {
  const out: string[] = []
  for (let index = 0; index < 8; index += 1) out.push(String(10020 + index))
  return out
})()

function isYSeries(name: string): boolean {
  return name.includes('Y-Series')
}

function isAIEyeHardware(name: string): boolean {
  return name.includes('AI Eye Hardware')
}

export function defaultDevicesFor(type: LineItemType, name: string, qty: number): string[] {
  if (type === 'sw') return ['House 1', 'House 2']
  if (isYSeries(name)) return [...Y_SERIES_SERIALS]
  if (isAIEyeHardware(name)) return [...HW_CAMERA_SERIALS]
  const out: string[] = []
  for (let index = 0; index < qty; index += 1) out.push(String(10012 + index))
  return out
}

export function newItemDeviceCatalogFor(sub: LineItemType, name: string): string[] {
  if (sub === 'sw') return []
  if (isYSeries(name)) return [...Y_SERIES_SERIALS]
  if (isAIEyeHardware(name)) return [...HW_CAMERA_SERIALS]
  return NEW_HW_DEVICE_CATALOG
}

export function existingDeviceCatalogFor(
  type: LineItemType,
  name: string,
  devices: string[],
): string[] {
  if (type === 'sw') return [...devices]
  if (isYSeries(name)) {
    return Array.from(new Set([...devices, ...Y_SERIES_SERIALS]))
  }
  if (isAIEyeHardware(name)) {
    return Array.from(new Set([...devices, ...HW_CAMERA_SERIALS]))
  }
  const base: string[] = []
  for (let index = 0; index < 8; index += 1) base.push(String(10012 + index))
  return Array.from(new Set([...devices, ...base]))
}

export function devicePlaceholdersFor(type: LineItemType): {
  assignLabel: string
  emptyText: string
  selectInputPlaceholder: string
} {
  if (type === 'sw') {
    return {
      assignLabel: 'Assigned Houses',
      emptyText: 'No houses assigned yet',
      selectInputPlaceholder: 'Type or paste house names...',
    }
  }
  return {
    assignLabel: 'Assigned Devices',
    emptyText: 'No devices assigned yet',
    selectInputPlaceholder: 'Type or paste IDs (e.g. 10020, 10021)...',
  }
}

const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function ordinal(value: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const remainder = value % 100
  return value + (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0])
}

export function deriveRecurringLabels(
  activationISO: string,
  billingType: BillingTypeOption,
): { billingDay: ReactNode; nextInvoice: ReactNode } {
  const placeholder = React.createElement(
    React.Fragment,
    null,
    '—',
    React.createElement(
      'span',
      { className: 'recurring-sub' },
      'Set once activation date is confirmed',
    ),
  )
  if (!activationISO || billingType === 'onetime') {
    return { billingDay: placeholder, nextInvoice: placeholder }
  }
  const [, , dayString] = activationISO.split('-')
  const day = parseInt(dayString, 10)
  const next = new Date(activationISO)
  if (billingType === 'annual') next.setFullYear(next.getFullYear() + 1)
  else if (billingType === 'quarterly') next.setMonth(next.getMonth() + 3)
  else next.setMonth(next.getMonth() + 1)
  const nextLabel = `${String(next.getMonth() + 1).padStart(2, '0')}/${String(
    next.getDate(),
  ).padStart(2, '0')}/${next.getFullYear()}`
  const monthName = MONTHS_FULL[parseInt(activationISO.split('-')[1], 10) - 1]

  let dayLabel: ReactNode
  let cycleLabel = ''
  if (billingType === 'annual') {
    dayLabel = `${ordinal(day)} of ${monthName}`
    cycleLabel = 'yearly'
  } else if (billingType === 'quarterly') {
    dayLabel = `${ordinal(day)} of the month`
    cycleLabel = 'every 3 months'
  } else {
    dayLabel = `${ordinal(day)} of each month`
    cycleLabel = 'monthly'
  }

  return {
    billingDay: React.createElement(
      React.Fragment,
      null,
      dayLabel,
      React.createElement('span', { className: 'recurring-sub' }, cycleLabel),
    ),
    nextInvoice: React.createElement(
      React.Fragment,
      null,
      nextLabel,
      React.createElement('span', { className: 'recurring-sub' }, 'auto-generated'),
    ),
  }
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function buildInitialDraft(isNew: boolean, deal: Deal | null): DealDraft {
  if (isNew || !deal) {
    return {
      companyId: '',
      businessId: '',
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      customerEIN: '',
      shippingAddress: '',
      activationISO: '',
      expirationISO: '',
      pricingRegion: 1,
      pricingTerm: 1,
      quoteNo: '',
      quoteUrl: '',
      billingCount: 1,
      billingType: 'annual',
      autoSendOn: true,
      ccExecOn: true,
      ccExecs: ['jd.chang@calyx.ai', 'admin@calyx.ai'],
      memo: '',
      lineItems: [],
    }
  }

  const lineItems: LineItemDraft[] = deal.lineItems.map((item) => {
    const sub = item.type
    const badge = badgeForSub(sub)
    const assignedFromBackend = item.assignments?.houses ?? item.assignments?.devices ?? []
    const devices = assignedFromBackend.length
      ? [...assignedFromBackend]
      : defaultDevicesFor(sub, item.productName, item.qty)
    return {
      id: uid(),
      type: sub,
      badgeLabel: badge.label,
      productValue: item.productKey,
      name: item.productName,
      qty: item.qty,
      devices,
      deviceCatalog: existingDeviceCatalogFor(sub, item.productName, devices),
    }
  })

  return {
    companyId: deal.companyId,
    businessId: deal.businessId,
    customerName: deal.customerName,
    customerEmail: deal.customerEmail,
    customerPhone: deal.telephone ?? '',
    customerEIN: deal.einTaxId ?? '',
    shippingAddress: deal.shippingAddress ?? '',
    activationISO: (deal.activationDate ?? '').slice(0, 10),
    expirationISO: (deal.expirationDate ?? '').slice(0, 10),
    pricingRegion: (deal.pricingRegion ?? 1) as PricingRegion,
    pricingTerm: (deal.pricingTerm ?? 1) as PricingTerm,
    quoteNo: deal.quoteNumber,
    quoteUrl: `https://quotes.calyx.ai/${deal.quoteNumber}`,
    billingCount: 1,
    billingType: billingCadenceToOption(deal.billingCadence),
    autoSendOn: true,
    ccExecOn: deal.ccExecOn,
    ccExecs: deal.ccExecs ?? ['jd.chang@calyx.ai', 'admin@calyx.ai'],
    memo: deal.memo ?? '',
    lineItems,
  }
}

export function validateDealDraft(draft: DealDraft): DealDraftValidation {
  const errors: DealDraftValidation['errors'] = {}
  if (!draft.companyId.trim()) errors.companyId = 'Company is required'
  if (!draft.businessId.trim()) errors.businessId = 'Business is required'
  if (!draft.customerName.trim()) errors.customerName = 'Customer name is required'
  if (!draft.customerEmail.trim()) {
    errors.customerEmail = 'Email is required'
  } else if (!EMAIL_PATTERN.test(draft.customerEmail.trim())) {
    errors.customerEmail = 'Invalid email'
  }
  if (!draft.shippingAddress.trim()) errors.shippingAddress = 'Shipping address is required'
  if (!draft.quoteNo.trim()) errors.quoteNo = 'Quote number is required'
  if (draft.expirationISO && !draft.activationISO) {
    errors.expirationISO = 'Activation date required before setting expiration'
  }
  return { valid: Object.keys(errors).length === 0, errors }
}

export function parseBulkTokens(text: string): string[] {
  return (text || '')
    .split(/[\s,;\t\r\n]+/)
    .map((token) => token.trim())
    .filter(Boolean)
}
